import { HttpBackend, HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { RBCheck } from '@lfw/roesberg';
import { DEFAULT_MAX_IMAGE_UPLOAD_SIZE_MB } from '@lfw/roesberg.liveforms.editor';
import { retry } from 'rxjs/operators';
import { AppUrls } from './route/app-urls';
import { firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';

// The backend reports its version as "V25.0.28" while the frontend holds a bare "25.0.28".
function normalizeVersion(version: string): string {
  return version.trim().replace(/^[Vv]/, '');
}

@Injectable({ providedIn: 'root' })
export class AppConfig {
  CONFIG: any;
  isLoading = true;
  private httpClient: HttpClient;

  // version of Android App, has to be provided
  appVersion = '';

  fatalError = '';

  hasError(): boolean {
    return !RBCheck.isNullOrEmpty(this.fatalError);
  }

  private httpBackend = inject(HttpBackend);

  constructor() {
    // use HttpBackend to bypass Tokeninjector and prevent circular dendency
    this.httpClient = new HttpClient(this.httpBackend);
  }

  loadConfig(): Promise<void> {
    return firstValueFrom(this.httpClient.get(AppUrls.getApiUrl(AppUrls.urlSettings, 'getSystem')).pipe(retry(3))).then(
      (data) => {
        this.CONFIG = data;
      }
    );
  }

  identityShowLogin(): boolean {
    return this.CONFIG.identityShowLogin;
  }

  identityUri(): string {
    return this.CONFIG.identityUri;
  }

  disableOpenIdSSL(): boolean {
    return this.CONFIG.disableOpenIdSSL;
  }

  identityClientId(): string {
    return this.CONFIG.identityClientID;
  }

  identityScopes(): string {
    return this.CONFIG.identityScopes;
  }

  hasPrivacyLink(): boolean {
    return !RBCheck.isNullOrEmpty(this.CONFIG.privacyUrl);
  }

  urlPrivacy(): string {
    return this.CONFIG.privacyUrl;
  }

  textPrivacy(): string {
    return this.CONFIG.privacyText;
  }

  hasImprintLink(): boolean {
    return !RBCheck.isNullOrEmpty(this.CONFIG.imprintUrl);
  }

  urlImprint(): string {
    return this.CONFIG.imprintUrl;
  }

  textImprint(): string {
    return this.CONFIG.imprintText;
  }

  hasServerInfo(): boolean {
    return !RBCheck.isNullOrEmpty(this.CONFIG.serverInfo);
  }

  serverInfo(): string {
    return this.CONFIG.serverInfo;
  }

  defaultLanguage(): string {
    return this.CONFIG.defaultLanguage;
  }

  disableSSL(): boolean {
    return this.CONFIG.disableSSL;
  }

  isMobileApp(): boolean {
    return !RBCheck.isNullOrEmpty(this.appVersion);
  }

  isLocked(): boolean {
    return this.CONFIG.isLocked;
  }

  messageHeader(): string {
    return this.CONFIG.messageHeader;
  }

  messageText(): string {
    return this.CONFIG.messageText;
  }

  version(): string {
    let version = '24.99.99';
    if (this.CONFIG != null && this.CONFIG.version != null) {
      version = this.CONFIG.version;
    }
    if (this.isMobileApp()) {
      version = version + ' ' + this.appVersion;
    }
    return version;
  }

  frontendVersion(): string {
    return environment.version;
  }

  /** Backend version without its "V" prefix, for display next to the frontend version. */
  apiVersion(): string {
    return normalizeVersion(this.version());
  }

  /**
   * Version for display: a single value when frontend and backend match, otherwise
   * "frontend (API backend)" so a client running stale frontend code stays recognisable.
   * Callers prepend "V"/"Version ", giving e.g. "V25.0.28 (API 25.0.29)".
   */
  displayVersion(): string {
    const frontend = this.frontendVersion();
    const backend = this.version();

    if (normalizeVersion(frontend) === normalizeVersion(backend)) {
      return frontend;
    }
    return `${frontend} (API ${normalizeVersion(backend)})`;
  }

  maxImageUploadSize(): number {
    const rawValue = this.CONFIG ? this.CONFIG.maxImageUploadSize : undefined;
    const sizeInMb = typeof rawValue === 'number' ? rawValue : rawValue !== undefined ? Number(rawValue) : NaN;
    if (!Number.isFinite(sizeInMb) || sizeInMb <= 0) {
      // Fallback to a safe default (e.g., for older backends without this setting)
      return DEFAULT_MAX_IMAGE_UPLOAD_SIZE_MB;
    }
    return sizeInMb;
  }
}
